/**
 * Purchase flow, vendor ledger and payables ageing.
 *
 * The distinctive piece here is the three-way match: quantity and price on the
 * bill must agree with what was ordered and what was actually received. It is
 * the main control against paying for goods that never arrived, and it is
 * configuration rather than code because a services business has nothing to
 * receive.
 */

import { computeDocumentTax, round2 } from "../sales/tax";
import { msmeDueStatus, type Partner } from "../partner";
import { assessItc, type ItcContext } from "./itc";
import type {
  Payment,
  PaymentAllocation,
  PurchaseAuditEvent,
  PurchaseConfig,
  PurchaseDocStatus,
  PurchaseDocType,
  PurchaseDocument
} from "./types";
import { canTransition, DOC_LABELS, isLocked } from "./types";

export interface PurchaseIssue {
  severity: "error" | "warning";
  code: string;
  message: string;
  field?: string;
}

// ------------------------------------------------------- Three-way match

export interface MatchLine {
  description: string;
  orderedQuantity: number;
  receivedQuantity: number;
  billedQuantity: number;
  orderedRate: number;
  billedRate: number;
  quantityVariance: number;
  quantityVariancePercent: number;
  priceVariance: number;
  priceVariancePercent: number;
  status: "matched" | "quantity-variance" | "price-variance" | "both";
}

export interface MatchResult {
  matched: boolean;
  lines: MatchLine[];
  issues: PurchaseIssue[];
}

/**
 * Compares the bill against its order and receipts. Variances inside the
 * configured tolerance pass; anything beyond is reported per line so the buyer
 * can see exactly what moved.
 */
export const threeWayMatch = (
  bill: PurchaseDocument,
  order: PurchaseDocument | undefined,
  receipts: PurchaseDocument[],
  config: PurchaseConfig
): MatchResult => {
  const issues: PurchaseIssue[] = [];
  if (!config.enforceThreeWayMatch) {
    return { matched: true, lines: [], issues: [] };
  }
  if (!order) {
    return {
      matched: false,
      lines: [],
      issues: [{ severity: "error", code: "NO_ORDER_TO_MATCH", message: "Three-way match is enforced but the bill has no purchase order." }]
    };
  }

  const receivedByLine = new Map<string, number>();
  for (const receipt of receipts) {
    if (receipt.status === "Cancelled") continue;
    for (const line of receipt.lines) {
      const key = line.originLineId ?? line.sourceLineId ?? line.id;
      receivedByLine.set(key, round2((receivedByLine.get(key) ?? 0) + (line.acceptedQuantity ?? line.quantity)));
    }
  }

  const orderByLine = new Map(order.lines.map((line) => [line.originLineId ?? line.id, line]));
  const lines: MatchLine[] = [];

  for (const billLine of bill.lines) {
    const sourceId = billLine.originLineId ?? billLine.sourceLineId ?? billLine.id;
    const orderLine = orderByLine.get(sourceId);
    const ordered = orderLine?.quantity ?? 0;
    const orderedRate = orderLine?.unitPrice ?? 0;
    const received = receivedByLine.get(sourceId) ?? 0;
    const billed = billLine.quantity;

    // Bill against receipt is the control that matters — you pay for what
    // arrived, not for what was ordered.
    const reference = config.requireGoodsReceipt ? received : ordered;
    const quantityVariance = round2(billed - reference);
    const quantityVariancePercent = reference > 0 ? round2((quantityVariance / reference) * 100) : billed > 0 ? 100 : 0;
    const priceVariance = round2(billLine.unitPrice - orderedRate);
    const priceVariancePercent = orderedRate > 0 ? round2((priceVariance / orderedRate) * 100) : 0;

    const quantityOff = Math.abs(quantityVariancePercent) > config.quantityTolerancePercent + 0.0001;
    const priceOff = orderLine ? Math.abs(priceVariancePercent) > config.priceTolerancePercent + 0.0001 : false;

    lines.push({
      description: billLine.description,
      orderedQuantity: ordered,
      receivedQuantity: received,
      billedQuantity: billed,
      orderedRate,
      billedRate: billLine.unitPrice,
      quantityVariance,
      quantityVariancePercent,
      priceVariance,
      priceVariancePercent,
      status: quantityOff && priceOff ? "both" : quantityOff ? "quantity-variance" : priceOff ? "price-variance" : "matched"
    });

    if (!orderLine) {
      issues.push({ severity: "error", code: "LINE_NOT_ON_ORDER", message: `"${billLine.description}" is billed but does not appear on the purchase order.` });
    }
    if (quantityOff) {
      issues.push({
        severity: "error",
        code: "QUANTITY_VARIANCE",
        message: `"${billLine.description}" — billed ${billed} against ${config.requireGoodsReceipt ? "received" : "ordered"} ${reference}, a variance of ${quantityVariancePercent}% beyond the ${config.quantityTolerancePercent}% tolerance.`
      });
    }
    if (priceOff) {
      issues.push({
        severity: "error",
        code: "PRICE_VARIANCE",
        message: `"${billLine.description}" — billed at ${billLine.unitPrice} against an ordered rate of ${orderedRate}, a variance of ${priceVariancePercent}% beyond the ${config.priceTolerancePercent}% tolerance.`
      });
    }
  }

  return { matched: issues.length === 0, lines, issues };
};

// ------------------------------------------------------------ Validation

export interface VendorContext {
  partner?: Partial<Partner>;
  /** Bills already posted to this vendor and still open. */
  outstandingBalance?: number;
  compositionScheme?: boolean;
}

export const validatePurchaseDocument = (
  document: PurchaseDocument,
  config: PurchaseConfig,
  context: VendorContext = {},
  related: { order?: PurchaseDocument; receipts?: PurchaseDocument[] } = {}
): PurchaseIssue[] => {
  const issues: PurchaseIssue[] = [];

  if (!document.lines.length) issues.push({ severity: "error", code: "NO_LINES", message: `A ${DOC_LABELS[document.type].toLowerCase()} needs at least one line.` });
  if (!document.partnerId) issues.push({ severity: "error", code: "NO_PARTNER", field: "partnerId", message: "Select a vendor." });
  if (!document.date) issues.push({ severity: "error", code: "NO_DATE", field: "date", message: "Document date is required." });

  if (document.type === "bill") {
    // The bill is filed under the vendor's number, not ours — GSTR-2B matches
    // on it, so it is mandatory rather than optional.
    if (!document.vendorDocumentNumber) {
      issues.push({ severity: "error", code: "NO_VENDOR_NUMBER", field: "vendorDocumentNumber", message: "The vendor's own invoice number is required — GSTR-2B reconciliation matches on it." });
    }
    if (!document.vendorDocumentDate) {
      issues.push({ severity: "error", code: "NO_VENDOR_DATE", field: "vendorDocumentDate", message: "The vendor's invoice date is required." });
    }
    if (config.requirePurchaseOrder && !document.purchaseOrderId) {
      issues.push({ severity: "error", code: "PO_REQUIRED", field: "purchaseOrderId", message: "Configuration requires every bill to originate from an approved purchase order." });
    }
    if (config.requireGoodsReceipt && !(document.goodsReceiptIds?.length)) {
      issues.push({ severity: "error", code: "GRN_REQUIRED", field: "goodsReceiptIds", message: "Configuration requires goods to be received before a bill is posted." });
    }
    // A registered vendor's GSTIN drives the whole ITC claim.
    if (context.partner?.gstStatus === "regular" && !document.partnerGstin) {
      issues.push({ severity: "error", code: "NO_VENDOR_GSTIN", field: "partnerGstin", message: "The vendor is registered but no GSTIN is recorded. Input credit cannot be claimed without it." });
    }
    if (!context.partner?.pan) {
      issues.push({ severity: "warning", code: "NO_VENDOR_PAN", field: "pan", message: "Vendor has no PAN. Section 206AA forces TDS at 20%." });
    }
  }

  for (const line of document.lines) {
    if (line.quantity <= 0) issues.push({ severity: "error", code: "BAD_QUANTITY", field: line.id, message: `"${line.description}" has a quantity of zero or less.` });
    if (line.unitPrice < 0) issues.push({ severity: "error", code: "BAD_PRICE", field: line.id, message: `"${line.description}" has a negative unit price.` });
    if (document.type === "bill" && !line.expenseNature) {
      issues.push({ severity: "warning", code: "NO_EXPENSE_NATURE", field: line.id, message: `"${line.description}" has no expense nature. ITC eligibility and TDS section cannot be determined without it.` });
    }
  }

  if (document.type === "bill" && config.enforceThreeWayMatch) {
    issues.push(...threeWayMatch(document, related.order, related.receipts ?? [], config).issues);
  }

  if (document.type === "bill" && config.blockItcWithout2B && document.reflectedIn2B === false) {
    issues.push({ severity: "error", code: "NOT_IN_2B", message: "Configuration blocks posting while the invoice is missing from GSTR-2B." });
  }

  return issues;
};

export const hasBlockingIssues = (issues: PurchaseIssue[]): boolean =>
  issues.some((issue) => issue.severity === "error");

// ----------------------------------------------------------- Transitions

export interface TransitionOptions {
  user: string;
  note?: string;
  now?: string;
  eventId?: string;
}

export const transition = (
  document: PurchaseDocument,
  to: PurchaseDocStatus,
  options: TransitionOptions
): { document: PurchaseDocument; event: PurchaseAuditEvent } => {
  const from = document.status;
  if (!canTransition(document.type, from, to)) {
    throw new Error(`${DOC_LABELS[document.type]} ${document.number} cannot move from "${from}" to "${to}".`);
  }
  const at = options.now ?? new Date().toISOString();
  const updated: PurchaseDocument = {
    ...document,
    status: to,
    updatedAt: at,
    ...(to === "Posted" ? { postedAt: at } : {}),
    ...(to === "Approved" ? { reviewer: options.user } : {}),
    ...(to === "On hold" ? { holdReason: options.note } : {})
  };
  return {
    document: updated,
    event: {
      id: options.eventId ?? `evt-${at}-${document.id}`,
      documentId: document.id,
      documentType: document.type,
      documentNumber: document.number,
      action: `${DOC_LABELS[document.type]} ${to.toLowerCase()}`,
      fromStatus: from,
      toStatus: to,
      user: options.user,
      at,
      note: options.note
    }
  };
};

export const recordEdit = (
  document: PurchaseDocument,
  changes: Array<{ field: string; from: unknown; to: unknown }>,
  options: TransitionOptions
): PurchaseAuditEvent => {
  if (isLocked(document)) {
    throw new Error(`${DOC_LABELS[document.type]} ${document.number} is ${document.status.toLowerCase()} and cannot be edited. Raise a debit note instead.`);
  }
  const at = options.now ?? new Date().toISOString();
  return {
    id: options.eventId ?? `evt-${at}-${document.id}`,
    documentId: document.id,
    documentType: document.type,
    documentNumber: document.number,
    action: "Edited",
    user: options.user,
    at,
    changes,
    note: options.note
  };
};

export const allowedConversions = (
  document: Pick<PurchaseDocument, "type" | "status">,
  config: PurchaseConfig
): PurchaseDocType[] => {
  switch (document.type) {
    case "purchase-request":
      return document.status === "Approved" ? ["purchase-order"] : [];
    case "purchase-order":
      if (!["Confirmed", "Sent", "Partially received", "Received"].includes(document.status)) return [];
      return config.requireGoodsReceipt ? ["goods-receipt"] : ["goods-receipt", "bill"];
    case "goods-receipt":
      return document.status === "Received" ? ["bill"] : [];
    case "bill":
      return ["Posted", "Partially paid", "Paid"].includes(document.status) ? ["debit-note"] : [];
    default:
      return [];
  }
};

export const convert = (
  source: PurchaseDocument,
  target: PurchaseDocType,
  config: PurchaseConfig,
  options: TransitionOptions & { number: string; date: string; lineIds?: string[]; quantities?: Record<string, number> }
): { document: PurchaseDocument; event: PurchaseAuditEvent } => {
  if (!allowedConversions(source, config).includes(target)) {
    throw new Error(`${DOC_LABELS[source.type]} ${source.number} in status "${source.status}" cannot be converted to a ${DOC_LABELS[target].toLowerCase()}.`);
  }
  const selected = options.lineIds?.length ? source.lines.filter((line) => options.lineIds!.includes(line.id)) : source.lines;
  if (!selected.length) throw new Error("No lines were selected for conversion.");
  if (!config.allowPartialBilling && target === "bill" && selected.length !== source.lines.length) {
    throw new Error("Partial billing is switched off. Bill the whole order or enable partial billing in configuration.");
  }

  const at = options.now ?? new Date().toISOString();
  const document: PurchaseDocument = {
    ...source,
    id: `${target}-${options.number}`,
    type: target,
    number: options.number,
    date: options.date,
    status: "Draft",
    lines: selected.map((line) => ({ ...line, id: `${line.id}-c`, quantity: options.quantities?.[line.id] ?? line.quantity, sourceLineId: line.id, originLineId: line.originLineId ?? line.id })),
    createdAt: at,
    updatedAt: at,
    postedAt: undefined,
    journalId: undefined,
    maker: options.user,
    reviewer: undefined,
    purchaseOrderId: source.type === "purchase-order" ? source.id : source.purchaseOrderId,
    goodsReceiptIds: source.type === "goods-receipt" ? [...(source.goodsReceiptIds ?? []), source.id] : source.goodsReceiptIds
  };

  return {
    document,
    event: {
      id: options.eventId ?? `evt-${at}-${document.id}`,
      documentId: document.id,
      documentType: target,
      documentNumber: options.number,
      action: `Created from ${DOC_LABELS[source.type].toLowerCase()} ${source.number}`,
      toStatus: "Draft",
      user: options.user,
      at,
      note: options.note
    }
  };
};

// ---------------------------------------------------------- Vendor ledger

export interface VendorLedgerEntry {
  id: string;
  date: string;
  kind: "bill" | "debit-note" | "credit-note-received" | "payment" | "opening";
  reference: string;
  vendorReference?: string;
  narration: string;
  /** Reduces what we owe. */
  debit: number;
  /** Increases what we owe. */
  credit: number;
  runningBalance: number;
  dueDate?: string;
}

/**
 * Credit-balance convention, the mirror of the customer ledger: a bill credits
 * the vendor, a payment debits them, so a positive closing balance is money we
 * owe.
 */
export const buildVendorLedger = (
  partnerId: string,
  documents: PurchaseDocument[],
  payments: Payment[],
  config: PurchaseConfig,
  options: { from?: string; to?: string; openingBalance?: number } = {}
): { entries: VendorLedgerEntry[]; closingBalance: number; totalDebit: number; totalCredit: number } => {
  const entries: VendorLedgerEntry[] = [];
  const inRange = (date: string) => (!options.from || date >= options.from) && (!options.to || date <= options.to);

  if (options.openingBalance) {
    entries.push({
      id: "opening", date: options.from ?? "", kind: "opening", reference: "Opening balance",
      narration: "Balance brought forward",
      debit: options.openingBalance < 0 ? round2(-options.openingBalance) : 0,
      credit: options.openingBalance > 0 ? round2(options.openingBalance) : 0,
      runningBalance: 0
    });
  }

  for (const document of documents) {
    if (document.partnerId !== partnerId) continue;
    if (!["Posted", "Partially paid", "Paid"].includes(document.status)) continue;
    if (!inRange(document.date)) continue;

    const tax = computeDocumentTax(document.lines, document.charges, document.documentDiscount, {canonical:document.canonical,adjustments:document.adjustments,
      supplyType: document.supplyType, rcm: document.rcm, roundOff: config.roundOff
    });
    // A debit note we raise, or a credit note the vendor issues, both reduce
    // what we owe.
    const reduces = document.type === "debit-note" || document.type === "credit-note-received";
    entries.push({
      id: document.id,
      date: document.date,
      kind: document.type === "bill" ? "bill" : document.type === "debit-note" ? "debit-note" : "credit-note-received",
      reference: document.number,
      vendorReference: document.vendorDocumentNumber,
      narration: reduces ? `Against ${document.againstBillId ?? "bill"}` : `${document.lines.length} line item${document.lines.length === 1 ? "" : "s"}`,
      debit: reduces ? tax.total : 0,
      credit: reduces ? 0 : tax.total,
      runningBalance: 0,
      dueDate: document.dueDate
    });
  }

  for (const payment of payments) {
    if (payment.partnerId !== partnerId || payment.status !== "Posted" || !inRange(payment.date)) continue;
    entries.push({
      id: payment.id, date: payment.date, kind: "payment", reference: payment.number,
      narration: `Payment by ${payment.mode.replace("-", " ")}${payment.reference ? ` · ${payment.reference}` : ""}`,
      debit: round2(payment.amount), credit: 0, runningBalance: 0
    });
    // TDS we deducted settles part of the bill without cash leaving.
    if (payment.tdsDeducted) {
      entries.push({
        id: `${payment.id}-tds`, date: payment.date, kind: "payment", reference: payment.number,
        narration: `TDS deducted${payment.tdsSection ? ` under section ${payment.tdsSection}` : ""}`,
        debit: round2(payment.tdsDeducted), credit: 0, runningBalance: 0
      });
    }
  }

  entries.sort((left, right) => {
    if (left.kind === "opening") return -1;
    if (right.kind === "opening") return 1;
    if (left.date !== right.date) return left.date < right.date ? -1 : 1;
    return right.credit - left.credit;
  });

  let balance = 0;
  let totalDebit = 0;
  let totalCredit = 0;
  for (const entry of entries) {
    balance = round2(balance + entry.credit - entry.debit);
    entry.runningBalance = balance;
    totalDebit = round2(totalDebit + entry.debit);
    totalCredit = round2(totalCredit + entry.credit);
  }
  return { entries, closingBalance: balance, totalDebit, totalCredit };
};

export interface OpenBill {
  id: string;
  number: string;
  vendorNumber?: string;
  date: string;
  dueDate?: string;
  total: number;
  paid: number;
  outstanding: number;
  inputTax: number;
}

export const openBills = (
  partnerId: string,
  documents: PurchaseDocument[],
  payments: Payment[],
  config: PurchaseConfig
): OpenBill[] => {
  const paidByBill = new Map<string, number>();
  for (const payment of payments) {
    if (payment.status !== "Posted") continue;
    // TDS withheld settles the bill just as cash does — the vendor's account is
    // discharged by cash plus the tax deposited on their behalf. Counting only
    // the cash would leave every bill short by the deduction, permanently.
    const allocated = round2(payment.allocations.reduce((sum, entry) => sum + entry.amount, 0));
    const tds = round2(payment.tdsDeducted ?? 0);
    for (const allocation of payment.allocations) {
      const tdsShare = allocated > 0 ? round2((tds * allocation.amount) / allocated) : 0;
      paidByBill.set(allocation.billId, round2((paidByBill.get(allocation.billId) ?? 0) + allocation.amount + tdsShare));
    }
  }
  for (const document of documents) {
    if (document.type !== "debit-note" || document.status !== "Posted" || !document.againstBillId) continue;
    const tax = computeDocumentTax(document.lines, document.charges, document.documentDiscount, {canonical:document.canonical,adjustments:document.adjustments,
      supplyType: document.supplyType, rcm: document.rcm, roundOff: config.roundOff
    });
    paidByBill.set(document.againstBillId, round2((paidByBill.get(document.againstBillId) ?? 0) + tax.total));
  }

  return documents
    .filter((document) => document.partnerId === partnerId && document.type === "bill" && ["Posted", "Partially paid"].includes(document.status))
    .map((document) => {
      const tax = computeDocumentTax(document.lines, document.charges, document.documentDiscount, {canonical:document.canonical,adjustments:document.adjustments,
        supplyType: document.supplyType, rcm: document.rcm, roundOff: config.roundOff
      });
      const paid = round2(paidByBill.get(document.id) ?? 0);
      return {
        id: document.id, number: document.number, vendorNumber: document.vendorDocumentNumber,
        date: document.date, dueDate: document.dueDate,
        total: tax.total, paid, outstanding: round2(tax.total - paid), inputTax: tax.totalTax
      };
    })
    .filter((bill) => bill.outstanding > 0.009)
    .sort((left, right) => (left.date < right.date ? -1 : left.date > right.date ? 1 : 0));
};

export const allocatePayment = (
  amount: number,
  bills: OpenBill[],
  strategy: "fifo" | "oldest-due" | "manual" = "fifo"
): { allocations: PaymentAllocation[]; unallocated: number } => {
  if (strategy === "manual") return { allocations: [], unallocated: round2(amount) };
  const ordered = [...bills].sort((left, right) => {
    if (strategy === "oldest-due") {
      const leftDue = left.dueDate ?? left.date;
      const rightDue = right.dueDate ?? right.date;
      if (leftDue !== rightDue) return leftDue < rightDue ? -1 : 1;
    }
    return left.date < right.date ? -1 : left.date > right.date ? 1 : 0;
  });
  let remaining = round2(amount);
  const allocations: PaymentAllocation[] = [];
  for (const bill of ordered) {
    if (remaining <= 0.009) break;
    const applied = round2(Math.min(remaining, bill.outstanding));
    if (applied > 0) {
      allocations.push({ billId: bill.id, billNumber: bill.number, amount: applied });
      remaining = round2(remaining - applied);
    }
  }
  return { allocations, unallocated: round2(remaining) };
};

// -------------------------------------------------- Payables ageing + MSME

export const PAYABLE_BUCKETS = [
  { label: "Not due", from: -Infinity, to: 0 },
  { label: "0-30 days", from: 1, to: 30 },
  { label: "31-60 days", from: 31, to: 60 },
  { label: "61-90 days", from: 61, to: 90 },
  { label: "91-180 days", from: 91, to: 180 },
  { label: "Over 180 days", from: 181, to: Infinity }
];

export interface PayableAgeingRow {
  partnerId: string;
  partnerName: string;
  /** Micro and small vendors are disclosed separately under Schedule III. */
  msmeCategory?: string;
  isMsme: boolean;
  total: number;
  buckets: Record<string, number>;
  /** Amount disallowable under section 43B(h) at the reporting date. */
  disallowable43B: number;
  oldestDays: number;
}

/**
 * Payables ageing with the MSME split built in. Section 43B(h) makes the micro
 * and small split a tax matter, not just a disclosure one — anything unpaid
 * beyond the agreed period is disallowed until it is actually paid.
 */
export const buildPayablesAgeing = (
  partners: Array<Pick<Partner, "id" | "name" | "msme" | "roles">>,
  documents: PurchaseDocument[],
  payments: Payment[],
  config: PurchaseConfig,
  options: { asAt: string; buckets?: typeof PAYABLE_BUCKETS }
): {
  rows: PayableAgeingRow[];
  totals: Record<string, number>;
  grandTotal: number;
  msmeTotal: number;
  disallowableTotal: number;
  bucketLabels: string[];
} => {
  const buckets = options.buckets ?? PAYABLE_BUCKETS;
  const bucketLabels = buckets.map((bucket) => bucket.label);
  const totals: Record<string, number> = Object.fromEntries(bucketLabels.map((label) => [label, 0]));
  const rows: PayableAgeingRow[] = [];

  for (const partner of partners) {
    const bills = openBills(partner.id, documents, payments, config);
    if (!bills.length) continue;

    const partnerBuckets: Record<string, number> = Object.fromEntries(bucketLabels.map((label) => [label, 0]));
    let total = 0;
    let disallowable = 0;
    let oldestDays = 0;
    const category = partner.msme?.category;
    const isMsme = category === "micro" || category === "small";

    for (const bill of bills) {
      const due = bill.dueDate ?? new Date(new Date(bill.date).getTime() + config.defaultCreditDays * 86_400_000).toISOString().slice(0, 10);
      const age = Math.floor((new Date(options.asAt).getTime() - new Date(due).getTime()) / 86_400_000);
      const bucket = buckets.find((candidate) => age >= candidate.from && age <= candidate.to) ?? buckets[buckets.length - 1];
      partnerBuckets[bucket.label] = round2(partnerBuckets[bucket.label] + bill.outstanding);
      totals[bucket.label] = round2(totals[bucket.label] + bill.outstanding);
      total = round2(total + bill.outstanding);

      const ageFromBill = Math.floor((new Date(options.asAt).getTime() - new Date(bill.date).getTime()) / 86_400_000);
      if (ageFromBill > oldestDays) oldestDays = ageFromBill;

      if (isMsme) {
        const status = msmeDueStatus(partner as Partner, bill.date, undefined, options.asAt);
        if (status.disallowable) disallowable = round2(disallowable + bill.outstanding);
      }
    }

    rows.push({
      partnerId: partner.id, partnerName: partner.name, msmeCategory: category,
      isMsme, total, buckets: partnerBuckets, disallowable43B: disallowable, oldestDays
    });
  }

  rows.sort((left, right) => right.total - left.total);
  return {
    rows,
    totals,
    grandTotal: round2(rows.reduce((sum, row) => sum + row.total, 0)),
    msmeTotal: round2(rows.filter((row) => row.isMsme).reduce((sum, row) => sum + row.total, 0)),
    disallowableTotal: round2(rows.reduce((sum, row) => sum + row.disallowable43B, 0)),
    bucketLabels
  };
};

// ------------------------------------------------------------- Posting

export interface PostingLine {
  accountCode: string;
  accountName: string;
  debit: number;
  credit: number;
  narration?: string;
  partnerId?: string;
}

/**
 * Bill posting.
 *
 *   Dr Expense / Asset          taxable value, plus any blocked input tax
 *   Dr Input CGST/SGST/IGST     claimable credit only
 *   Dr ITC pending (2B)         credit deferred until conditions are met
 *       Cr Trade payables            gross payable to the vendor
 *       Cr TDS payable               deducted at source
 *
 * Blocked credit is added to the expense rather than sitting in the input
 * ledger, which is what keeps the credit ledger agreeing to the GST portal.
 */
export const buildBillPosting = (
  document: PurchaseDocument,
  config: PurchaseConfig,
  accounts: {
    payable: string; inputCgst: string; inputSgst: string; inputIgst: string;
    itcPending: string; tdsPayable: string; roundOff: string; defaultExpense: string;
    rcmLiability: string;
  },
  itcContext: ItcContext = {}
): { lines: PostingLine[]; balanced: boolean; itc: ReturnType<typeof assessItc> } => {
  const tax = computeDocumentTax(document.lines, document.charges, document.documentDiscount, {canonical:document.canonical,adjustments:document.adjustments,
    supplyType: document.supplyType, rcm: document.rcm, roundOff: config.roundOff
  });
  const itc = assessItc(document, tax.totalTax, itcContext);

  const lines: PostingLine[] = [];
  const isReducing = document.type === "debit-note" || document.type === "credit-note-received";
  const flip = (debit: number, credit: number) => (isReducing ? { debit: credit, credit: debit } : { debit, credit });

  // Expense by account, with blocked tax capitalised into it.
  const expenseByAccount = new Map<string, number>();
  for (const line of document.lines) {
    const computed = tax.lines.find((entry) => entry.lineId === line.id);
    if (!computed) continue;
    const account = line.accountCode ?? accounts.defaultExpense;
    expenseByAccount.set(account, round2((expenseByAccount.get(account) ?? 0) + computed.taxableValue));
  }
  if (itc.blocked > 0) {
    const first = [...expenseByAccount.keys()][0] ?? accounts.defaultExpense;
    expenseByAccount.set(first, round2((expenseByAccount.get(first) ?? 0) + itc.blocked));
  }
  for (const [accountCode, amount] of expenseByAccount) {
    lines.push({ accountCode, accountName: "Expense", ...flip(amount, 0) });
  }

  // Claimable credit, split by head in the same proportion as the tax.
  if (itc.claimable > 0 && tax.totalTax > 0) {
    const ratio = itc.claimable / tax.totalTax;
    const cgst = round2(tax.cgst * ratio);
    const sgst = round2(tax.sgst * ratio);
    const igst = round2(itc.claimable - cgst - sgst);
    if (cgst) lines.push({ accountCode: accounts.inputCgst, accountName: "Input CGST", ...flip(cgst, 0) });
    if (sgst) lines.push({ accountCode: accounts.inputSgst, accountName: "Input SGST", ...flip(sgst, 0) });
    if (igst) lines.push({ accountCode: accounts.inputIgst, accountName: "Input IGST", ...flip(igst, 0) });
  }
  if (itc.deferred > 0) {
    lines.push({ accountCode: accounts.itcPending, accountName: "ITC not yet available", ...flip(itc.deferred, 0) });
  }

  // Under reverse charge we owe the tax ourselves rather than to the vendor.
  if (document.rcm && tax.totalTax === 0) {
    const rcmTax = round2(
      document.lines.reduce((sum, line) => {
        const computed = tax.lines.find((entry) => entry.lineId === line.id);
        return sum + (computed ? (computed.taxableValue * line.gstRate) / 100 : 0);
      }, 0)
    );
    if (rcmTax > 0) {
      lines.push({ accountCode: accounts.rcmLiability, accountName: "GST payable under reverse charge", ...flip(0, rcmTax) });
      lines.push({ accountCode: accounts.inputIgst, accountName: "Input GST — RCM", ...flip(rcmTax, 0) });
    }
  }

  const tds = round2(document.tdsAmount ?? 0);
  if (tds > 0) lines.push({ accountCode: accounts.tdsPayable, accountName: "TDS payable", ...flip(0, tds) });

  lines.push({
    accountCode: accounts.payable, accountName: "Trade payables",
    ...flip(0, round2(tax.total - tds)),
    partnerId: document.partnerId,
    narration: `${DOC_LABELS[document.type]} ${document.number}${document.vendorDocumentNumber ? ` · vendor ref ${document.vendorDocumentNumber}` : ""}`
  });

  if (tax.roundOff) {
    const positive = tax.roundOff > 0;
    lines.push({ accountCode: accounts.roundOff, accountName: "Rounding difference", ...flip(positive ? positive ? 0 : 0 : Math.abs(tax.roundOff), positive ? tax.roundOff : 0) });
  }

  const totalDebit = round2(lines.reduce((sum, line) => sum + line.debit, 0));
  const totalCredit = round2(lines.reduce((sum, line) => sum + line.credit, 0));
  return { lines, balanced: Math.abs(totalDebit - totalCredit) < 0.01, itc };
};

/**
 * Payment posting.
 *
 *   Dr Trade payables       bill value settled
 *       Cr Bank                  cash actually paid
 *       Cr TDS payable           amount withheld
 */
export const buildPaymentPosting = (
  payment: Pick<Payment, "amount" | "tdsDeducted" | "bankAccountCode" | "partnerId" | "number">,
  accounts: { payable: string; bank: string; tdsPayable: string }
): { lines: PostingLine[]; balanced: boolean } => {
  const tds = round2(payment.tdsDeducted ?? 0);
  const cash = round2(payment.amount);
  const lines: PostingLine[] = [
    { accountCode: accounts.payable, accountName: "Trade payables", debit: round2(cash + tds), credit: 0, partnerId: payment.partnerId, narration: `Payment ${payment.number}` },
    { accountCode: payment.bankAccountCode ?? accounts.bank, accountName: "Bank", debit: 0, credit: cash }
  ];
  if (tds > 0) lines.push({ accountCode: accounts.tdsPayable, accountName: "TDS payable", debit: 0, credit: tds });

  const totalDebit = round2(lines.reduce((sum, line) => sum + line.debit, 0));
  const totalCredit = round2(lines.reduce((sum, line) => sum + line.credit, 0));
  return { lines, balanced: Math.abs(totalDebit - totalCredit) < 0.01 };
};

